/** Google Analytics 4 — loaded only when VITE_GA_MEASUREMENT_ID is configured. */

/** First-party proxy path (ingress → googletagmanager.com). Works when edge CSP allows only 'self'. */
export const GA_FIRST_PARTY_GTAG_PATH = "/analytics/gtag/js";
export const GA_TRANSPORT_URL = "https://tellerwand.com/analytics";

export interface GtagConfig {
  page_path?: string;
  page_title?: string;
  send_page_view?: boolean;
  [key: string]: unknown;
}

export interface GtagEventParams {
  event_category?: string;
  event_label?: string;
  value?: number;
  [key: string]: unknown;
}

declare global {
  interface Window {
    gtag?: (command: string, targetId: string, config?: GtagConfig | GtagEventParams) => void;
    dataLayer?: unknown[];
  }
}

let gaInitialized = false;

/**
 * Read Ga Measurement Id.
 */
function readGaMeasurementId(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const runtimeId = (window as Window & { __ENV__?: { VITE_GA_MEASUREMENT_ID?: string } }).__ENV__
    ?.VITE_GA_MEASUREMENT_ID;

  if (runtimeId?.trim()) {
    return runtimeId.trim();
  }

  const buildTimeId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  return typeof buildTimeId === "string" && buildTimeId.trim() ? buildTimeId.trim() : undefined;
}

function isValidGaId(id: string): boolean {
  return /^G-[A-Z0-9]+$/.test(id);
}

function buildGaScriptSrc(measurementId: string): string {
  return `${GA_FIRST_PARTY_GTAG_PATH}?id=${encodeURIComponent(measurementId)}`;
}

function buildGaTransportConfig(overrides: GtagConfig = {}): GtagConfig {
  return {
    transport_url: GA_TRANSPORT_URL,
    first_party_collection: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    ads_data_redaction: true,
    ...overrides,
  };
}

function isStaticGtagScriptPresent(): boolean {
  return Boolean(
    document.querySelector('script[src*="googletagmanager.com/gtag/js"]') ||
    document.querySelector(`script[src^="${GA_FIRST_PARTY_GTAG_PATH}"]`)
  );
}

/**
 * Inject Ga Script.
 */
function injectGaScript(measurementId: string): void {
  if (document.querySelector(`script[data-ga-id="${measurementId}"]`)) {
    return;
  }

  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer?.push(args);
  };
  window.gtag("js", new Date());

  const script = document.createElement("script");
  script.async = true;
  script.src = buildGaScriptSrc(measurementId);
  script.dataset.gaId = measurementId;
  document.head.appendChild(script);
}

/**
 * Initialize GA4 once runtime config is available. No-ops when measurement ID is unset.
 */
export function initGoogleAnalytics(): boolean {
  if (gaInitialized || typeof window === "undefined") {
    return gaInitialized;
  }

  const existingGtagScript = isStaticGtagScriptPresent();

  // Static tag in index.html (first-party proxy + transport_url)
  if (window.gtag && existingGtagScript) {
    gaInitialized = true;
    return true;
  }

  const measurementId = readGaMeasurementId();
  if (!measurementId) {
    return false;
  }

  if (!isValidGaId(measurementId)) {
    console.warn("Google Analytics: invalid measurement ID format");
    return false;
  }

  injectGaScript(measurementId);
  window.gtag?.("config", measurementId, buildGaTransportConfig({ send_page_view: false }));
  gaInitialized = true;
  return true;
}

/**
 * Track Ga Page View.
 */
export function trackGaPageView(path: string, title?: string): void {
  if (!gaInitialized) {
    return;
  }

  const measurementId = readGaMeasurementId();
  if (!measurementId || !window.gtag) {
    return;
  }

  window.gtag(
    "config",
    measurementId,
    buildGaTransportConfig({
      page_path: path,
      page_title: title,
    })
  );
}

/**
 * Track Ga Event.
 */
export function trackGaEvent(
  action: string,
  category: string,
  label?: string,
  value?: number
): void {
  if (!gaInitialized || !window.gtag) {
    return;
  }

  window.gtag("event", action, {
    event_category: category,
    event_label: label,
    value,
  });
}

export function isGoogleAnalyticsEnabled(): boolean {
  return gaInitialized;
}

/** @internal Test helper */
export function resetGoogleAnalyticsForTests(): void {
  gaInitialized = false;
}
